import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Phone, ChevronDown } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { useLoginModal } from "@/context/LoginModalContext";
import { BRAND_LOGO } from "@/components/SEO";
import api from "@/lib/api";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dests, setDests] = useState([]);
  const settings = useSettings();
  const { user, logout } = useAuth();
  const { openLogin } = useLoginModal();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    api.get("/destinations").then((r) => setDests(r.data)).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const byRegion = (region) => dests.filter((d) => d.region === region).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const RegionMenu = ({ label, region }) => (
    <div className="group relative">
      <Link to={`/tours?region=${region}`} data-testid={`nav-${region.toLowerCase()}`} className="flex items-center gap-1 font-body text-[15px] font-medium text-navy/80 transition-colors hover:text-brandorange">
        {label} <ChevronDown className="h-4 w-4" />
      </Link>
      <div className="invisible absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 translate-y-2 rounded-2xl border border-gray-100 bg-white p-2 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
        {byRegion(region).map((d) => (
          <Link key={d.id} to={`/tours?region=${region}&destination=${d.slug}`} className="block rounded-xl px-4 py-2.5 text-sm font-medium text-navy hover:bg-navy/5">
            {d.name} Tour Packages
          </Link>
        ))}
        <Link to={`/tours?region=${region}`} className="mt-1 block rounded-xl px-4 py-2.5 text-sm font-bold text-brandorange hover:bg-orange-50">View all {label} →</Link>
      </div>
    </div>
  );

  return (
    <header className={`fixed top-0 z-40 w-full transition-all duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_4px_20px_rgba(0,0,0,0.06)]" : "bg-white/60 backdrop-blur-md"}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
        <Link to="/" data-testid="navbar-logo" className="flex items-center"><img src={settings?.logo || BRAND_LOGO} alt="Happy Holidays Wale" className="h-10 w-auto md:h-12" /></Link>

        <nav className="hidden items-center gap-8 lg:flex">
          <Link to="/" data-testid="nav-link-home" className="font-body text-[15px] font-medium text-navy/80 transition-colors hover:text-brandorange">Home</Link>
          <RegionMenu label="India" region="India" />
          <RegionMenu label="International" region="International" />
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a href={`tel:${settings?.phone || ""}`} data-testid="nav-phone" className="flex items-center gap-2 text-sm font-semibold text-navy"><Phone className="h-4 w-4 text-brandorange" /> {settings?.phone}</a>
          {user ? (
            <div className="flex items-center gap-2">
              <Link to={user.role === "admin" ? "/admin" : "/account"} data-testid="nav-account-link" className="flex items-center gap-1 rounded-full border border-navy/15 px-4 py-2 text-sm font-semibold text-navy hover:bg-navy/5"><User className="h-4 w-4" /> {user.role === "admin" ? "Admin" : "Account"}</Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout" className="text-navy/60 hover:text-brandred"><LogOut className="h-5 w-5" /></button>
            </div>
          ) : (
            <button onClick={openLogin} data-testid="nav-login" className="rounded-full border border-navy/15 px-4 py-2 text-sm font-semibold text-navy hover:bg-navy/5">Login</button>
          )}
          <Link to="/enquiry" data-testid="nav-enquire" className="btn-gradient rounded-full px-5 py-2.5 text-sm font-bold">Enquiry Now</Link>
        </div>

        <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">{open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}</button>
      </div>

      {open && (
        <div className="border-t border-navy/10 bg-white lg:hidden">
          <nav className="flex flex-col gap-1 px-4 py-4">
            <Link to="/" onClick={() => setOpen(false)} className="rounded-xl px-3 py-3 font-medium text-navy hover:bg-navy/5">Home</Link>
            <p className="px-3 pt-2 text-xs font-bold uppercase text-gray-400">India</p>
            {byRegion("India").map((d) => <Link key={d.id} to={`/tours?region=India&destination=${d.slug}`} onClick={() => setOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-medium text-navy hover:bg-navy/5">{d.name} Tour Packages</Link>)}
            <p className="px-3 pt-2 text-xs font-bold uppercase text-gray-400">International</p>
            {byRegion("International").map((d) => <Link key={d.id} to={`/tours?region=International&destination=${d.slug}`} onClick={() => setOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-medium text-navy hover:bg-navy/5">{d.name} Tour Packages</Link>)}
            <a href={`tel:${settings?.phone}`} className="rounded-xl px-3 py-3 font-medium text-navy hover:bg-navy/5">📞 {settings?.phone}</a>
            <div className="mt-2 flex gap-2">
              {user ? (
                <Link to={user.role === "admin" ? "/admin" : "/account"} onClick={() => setOpen(false)} className="flex-1 rounded-full border border-navy/15 py-3 text-center font-semibold text-navy">{user.role === "admin" ? "Admin" : "Account"}</Link>
              ) : (
                <button onClick={() => { setOpen(false); openLogin(); }} data-testid="mobile-nav-login" className="flex-1 rounded-full border border-navy/15 py-3 text-center font-semibold text-navy">Login</button>
              )}
              <Link to="/enquiry" onClick={() => setOpen(false)} className="btn-gradient flex-1 rounded-full py-3 text-center font-bold">Enquiry</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
